import { useEffect, useId, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import styles from './CreateTeamDialog.module.scss'

import { isApiError } from '@/api'
import { listCostCenters } from '@/api/costCenters'
import { createTeam, getActiveCostCenterForCaller } from '@/api/team'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import FormErrorBanner from '@/components/FormErrorBanner'
import FormField from '@/components/FormField/FormField'
import MemberMultiSelect from '@/components/MemberMultiSelect'
import Select from '@/components/Select'
import { enterpriseTeamsQueryKey } from '@/hooks/useEnterpriseTeams'
import { COST_CENTERS_QUERY_KEY } from '@/pages/app/Settings/CostCenters'
import { MY_ACTIVE_COST_CENTER_QUERY_KEY, TEAM_MEMBERS_TABLE_QUERY_KEY } from '@/pages/app/Team/Department/utils'
import { selectCurrentUserId } from '@/store/features/user/userSlice'

interface CreateTeamDialogProps {
  isOpen: boolean
  enterpriseId: string
  onSave: () => void
  onCancel: () => void
}

// Matches CreateTeamRequest.member_user_ids max_length on the backend.
const MAX_TEAM_MEMBERS = 500

const formSchema = z.object({
  name: z.string().min(1, 'Department name is required').max(120, 'Department name is too long (max 120 chars)'),
  costCenterId: z.string().min(1, 'Pick a cost center'),
  memberUserIds: z.array(z.string()).max(MAX_TEAM_MEMBERS, `You can add at most ${MAX_TEAM_MEMBERS} members`),
  successorAdminEmail: z.string().email('Enter a valid email').or(z.literal('')),
})

type FormValues = z.infer<typeof formSchema>

export default function CreateTeamDialog({ isOpen, enterpriseId, onSave, onCancel }: CreateTeamDialogProps) {
  const queryClient = useQueryClient()
  const callerUserId = useSelector(selectCurrentUserId) ?? ''
  const [formError, setFormError] = useState<string | null>(null)

  const ccId = useId()
  const successorId = useId()

  // Must mirror CostCentersView's queryFn shape (returns the cost_centers
  // array, not the ApiResponse wrapper). Both observers share
  // COST_CENTERS_QUERY_KEY, so a divergent shape means whichever component
  // primes the cache first wins — and the loser reads `undefined.cost_centers`,
  // hiding the picker even when the enterprise has 2+ CCs.
  // `isFetching` (not `isLoading`) keeps the picker in its loading state
  // through background refetches so a newly created CC can't be missed on a
  // warm cache. Same reasoning as the activeCc query below.
  const { data: costCenters, isFetching: isLoadingCCs } = useQuery({
    queryKey: COST_CENTERS_QUERY_KEY(enterpriseId),
    queryFn: async () => (await listCostCenters(enterpriseId)).data.cost_centers,
    enabled: isOpen,
    refetchOnMount: 'always',
  })

  // Fetch every active member of the caller's cost center (across all teams)
  // so the picker can pull anyone in the CC into the new team. The response
  // also carries the caller's team_id + effective-admin status, used to scope
  // successor-admin candidates to the caller's old teammates only.
  //
  // Use `isFetching` (not `isLoading`) as the loading gate: creating one team
  // moves the caller onto it, but the cache key has no user/team component so
  // the next dialog open shows STALE data while `refetchOnMount: 'always'`
  // refetches in the background. With `isLoading` the gate flips false the
  // instant cache exists, the form briefly renders against the previous
  // team's data, and `isSoleAdminWithOthers` evaluates wrong — the successor
  // field doesn't render even when the BE would (and does) require it.
  // `isFetching` keeps the loading state up through the refetch.
  const { data: activeCcResp, isFetching: isLoadingActiveCc } = useQuery({
    queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY],
    queryFn: () => getActiveCostCenterForCaller(),
    enabled: isOpen,
    refetchOnMount: 'always',
  })

  const ccOptions = useMemo(() => (costCenters ?? []).map((cc) => ({ value: cc.id, label: cc.name })), [costCenters])

  const callerTeamId = activeCcResp?.data?.caller_team_id ?? null
  const callerIsEffectiveAdminOnOldTeam = activeCcResp?.data?.caller_is_effective_team_admin ?? false

  // Every active member of the caller's CC EXCLUDING the caller. Each row
  // carries its current team_id so successor-admin candidates can be filtered
  // to caller's old teammates only.
  const ccMembers = useMemo(() => {
    const members = activeCcResp?.data?.members ?? []
    return members
      .filter((m) => m.user_id !== callerUserId)
      .map((m) => ({
        userId: m.user_id,
        email: m.email ?? '',
        firstName: m.first_name ?? '',
        lastName: m.last_name ?? '',
        teamId: m.team_id,
        teamName: m.team_name,
        isEffectiveTeamAdmin: m.is_effective_team_admin,
      }))
  }, [activeCcResp, callerUserId])

  // Caller is the sole effective admin of their CURRENT team (the team they're
  // leaving) when they are an effective admin AND no other member of that
  // same team is an effective admin AND there's at least one other member on
  // it. Matches the backend's _effective_team_admins check at
  // services/team/service.py.
  const isSoleAdminWithOthers = useMemo(() => {
    if (!callerIsEffectiveAdminOnOldTeam || callerTeamId === null) return false
    const oldTeammates = ccMembers.filter((m) => m.teamId === callerTeamId)
    if (oldTeammates.length === 0) return false
    return oldTeammates.every((m) => !m.isEffectiveTeamAdmin)
  }, [ccMembers, callerTeamId, callerIsEffectiveAdminOnOldTeam])

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '', costCenterId: '', memberUserIds: [], successorAdminEmail: '' },
  })

  const watchedCcId = watch('costCenterId')

  // Reset on open. Declared FIRST so React runs it before the default-set
  // effect below — otherwise on a warm cache (ccOptions already populated)
  // reset would clobber the default-set value to '' on the same render and
  // the picker-hidden single-CC case would submit with an empty costCenterId.
  useEffect(() => {
    if (isOpen) {
      reset({ name: '', costCenterId: '', memberUserIds: [], successorAdminEmail: '' })
      setFormError(null)
    }
  }, [isOpen, reset])

  // Default CC to the first option once loaded.
  useEffect(() => {
    if (!watchedCcId && ccOptions.length > 0) {
      setValue('costCenterId', ccOptions[0].value)
    }
  }, [ccOptions, watchedCcId, setValue])

  const submitMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const res = await createTeam(values.costCenterId, {
        name: values.name,
        member_user_ids: values.memberUserIds,
        successor_admin_email: values.successorAdminEmail || null,
      })
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enterprise-members'] })
      queryClient.invalidateQueries({ queryKey: COST_CENTERS_QUERY_KEY(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: enterpriseTeamsQueryKey(enterpriseId) })
      // Per-team membership panels read from a separate cache key; without
      // this, picked members linger on their old team in the UI even though
      // they've been moved server-side.
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY] })
      // Creating a team moves the caller onto it, so their active CC + team +
      // sole-admin status all change. Without this invalidation the next open
      // briefly renders against the previous team's data while
      // refetchOnMount:'always' is mid-flight — long enough that a fast
      // submit suppresses the successor-admin field.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
      reset()
      setFormError(null)
      onSave()
    },
    onError: (err: unknown) => {
      setFormError(
        isApiError(err)
          ? (err.detail ?? 'Failed to create department. Please try again.')
          : 'Failed to create department. Please try again.'
      )
    },
  })

  function handleCancel() {
    reset()
    setFormError(null)
    submitMutation.reset()
    onCancel()
  }

  // Surface zod validation failures as a banner. Without this, clicking
  // Create team when costCenterId is still '' (race: CC list loaded but the
  // default-set useEffect hasn't committed yet) silently no-ops — the dropdown
  // is hidden for single-CC enterprises, so the user has no visible cue that a
  // field is invalid. Observed during break-away v3 smoke against Wispr
  // (single-CC replica): first two Create-team clicks did nothing; refresh
  // resolved the race.
  function onInvalid(errors: Record<string, { message?: string }>) {
    const firstMessage = Object.values(errors)
      .map((e) => e?.message)
      .find((m): m is string => Boolean(m))
    setFormError(firstMessage ?? 'Please fill in the required fields.')
  }

  function onSubmit(values: FormValues) {
    if (submitMutation.isPending) return
    setFormError(null)
    if (isSoleAdminWithOthers && !values.successorAdminEmail) {
      setError('successorAdminEmail', {
        type: 'required',
        message: 'Pick a successor admin before leaving — you are the only admin on your current team.',
      })
      return
    }
    submitMutation.mutate(values)
  }

  return (
    <DialogWrapper isOpen={isOpen} title="Create a department" close={handleCancel} minWidth={520}>
      <form className={styles.body} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <FormField
          name="name"
          label="Department name"
          control={control}
          error={errors.name}
          placeholder="e.g., Finance, Product, Marketing…"
          disabled={submitMutation.isPending}
        />

        {ccOptions.length > 1 && (
          <div className={styles.field}>
            <label htmlFor={ccId} className={styles.label}>
              Cost center
            </label>
            <Controller
              control={control}
              name="costCenterId"
              render={({ field }) => (
                <Select
                  id={ccId}
                  value={field.value}
                  onChange={field.onChange}
                  options={ccOptions}
                  placeholder={isLoadingCCs ? 'Loading…' : 'Select a cost center'}
                  isLoading={isLoadingCCs}
                  isDisabled={submitMutation.isPending}
                />
              )}
            />
            {errors.costCenterId && <p className={styles.fieldError}>{errors.costCenterId.message}</p>}
          </div>
        )}

        {isLoadingActiveCc ? (
          <p className={styles.loading}>Loading members…</p>
        ) : (
          <Controller
            control={control}
            name="memberUserIds"
            render={({ field }) => (
              <MemberMultiSelect
                members={ccMembers}
                selectedUserIds={field.value}
                onChange={field.onChange}
                callerUserId={callerUserId}
                isDisabled={submitMutation.isPending}
                maxSelectable={MAX_TEAM_MEMBERS}
              />
            )}
          />
        )}

        {!isLoadingActiveCc && isSoleAdminWithOthers && (
          <div className={styles.field}>
            <label htmlFor={successorId} className={styles.label}>
              Successor admin
            </label>
            <Controller
              control={control}
              name="successorAdminEmail"
              render={({ field }) => {
                // Eligible successors: active members of the caller's CURRENT
                // team only (a successor admin has to stay on the old team to
                // actually be its admin). Drop anyone the caller has picked to
                // bring along — they're moving to the new team.
                const selectedIds = new Set(watch('memberUserIds'))
                const eligible = ccMembers.filter((m) => m.teamId === callerTeamId && !selectedIds.has(m.userId))
                const successorOptions = eligible.map((m) => ({
                  value: m.email,
                  label: m.firstName || m.lastName ? `${m.firstName} ${m.lastName}`.trim() + ` (${m.email})` : m.email,
                }))
                return (
                  <Select
                    id={successorId}
                    value={field.value}
                    onChange={field.onChange}
                    options={successorOptions}
                    placeholder={
                      successorOptions.length === 0
                        ? 'No eligible successor — un-select someone'
                        : 'Pick a successor admin'
                    }
                    isDisabled={submitMutation.isPending || successorOptions.length === 0}
                  />
                )
              }}
            />
            <p className={styles.helper}>
              You&apos;re the only admin on your current team. Pick someone to promote to admin before you leave.
            </p>
            {errors.successorAdminEmail && <p className={styles.fieldError}>{errors.successorAdminEmail.message}</p>}
          </div>
        )}

        {formError && <FormErrorBanner message={formError} />}

        <div className={styles.actions}>
          <Button title="Cancel" onClick={handleCancel} type="light" isDisabled={submitMutation.isPending} />
          <Button
            title={submitMutation.isPending ? 'Creating…' : 'Create department'}
            onClick={() => {}}
            htmlType="submit"
            type="dark"
            isLoading={submitMutation.isPending}
            isDisabled={submitMutation.isPending || isLoadingCCs || isLoadingActiveCc}
          />
        </div>
      </form>
    </DialogWrapper>
  )
}
